/* eslint-disable no-unused-vars */
import { useEffect, useRef, useState, useMemo } from 'react';
import * as S from './SelectedFilterItems.styled';
import { Each, Label, Buttons, ParagraphWithTooltip, IconButton } from 'components';
import { DeleteIcon } from 'assets/icons';
import { useSelector, useDispatch } from 'react-redux';
import { publisherReportsFiltersTableActions } from 'store/actions';
import ReactCountryFlag from 'react-country-flag';
import { useComputed, useSignal, useSignalEffect } from '@preact/signals-react';

const getSelectedItem = (itemsMap, uniqueKey) => {
  return itemsMap.get(uniqueKey);
};

const isItemSelected = (itemsMap, selectedItems) => selectedItems.some((key) => itemsMap.get(key));

const subLabelStyle = { whiteSpace: 'nowrap', width: '57%' };
const countrySubLabelStyle = { whiteSpace: 'nowrap', width: '50%' };
const labelStyle = { whiteSpace: 'nowrap' };

export const SelectedFilterItems = ({
  placementList$,
  countryList$,
  advertiserList$,
  selectedAppsNumbers$,
  applicationsMap$,
  toggleSelectedAppNumber,
}) => {
  const boxRef = useRef(null);
  const wrapperBoxRef = useRef(null);
  const [fitsInOneRow, setFitsInOneRow] = useState(false);

  const boxHeight$ = useSignal('32px');
  const plusBtnLeftPosition$ = useSignal(0);
  const showLessBtnLeftPosition$ = useSignal(0);

  const childCount$ = useSignal(0);
  const elemCountInFirstRow$ = useSignal(0);

  const dispatch = useDispatch();

  const selectedPlacementsArr = useSelector(
    (state) => state.publisherReportsFiltersTable.placementsFilter,
  );

  const selectedAdvertisersArr = useSelector(
    (state) => state.publisherReportsFiltersTable.advertisersFilter,
  );

  const selectedCountriesArr = useSelector(
    (state) => state.publisherReportsFiltersTable.countriesFilter,
  );

  const handleDelete = ({ element, uniqueKey }) => {
    if (element === 'app') {
      toggleSelectedAppNumber(uniqueKey);
      return;
    }
    if (element === 'placement') {
      const newPlacementsArr = selectedPlacementsArr.filter((placement) => placement !== uniqueKey);
      dispatch(
        publisherReportsFiltersTableActions.updateProperty({
          newValue: newPlacementsArr,
          propField: 'placementsFilter',
        }),
      );
      return;
    }
    if (element === 'advertiser') {
      const newAdvertisersFiltersArr = selectedAdvertisersArr.filter(
        (adveriser_id) => adveriser_id !== uniqueKey,
      );
      dispatch(
        publisherReportsFiltersTableActions.updateProperty({
          newValue: newAdvertisersFiltersArr,
          propField: 'advertisersFilter',
        }),
      );
      return;
    }
    if (element === 'country') {
      const newCountriesFiltersArr = selectedCountriesArr.filter(
        (country_code) => country_code !== uniqueKey,
      );
      dispatch(
        publisherReportsFiltersTableActions.updateProperty({
          newValue: newCountriesFiltersArr,
          propField: 'countriesFilter',
        }),
      );
      return;
    }
  };

  const deleteAll = () => {
    selectedAppsNumbers$.value.forEach((pba_number) => {
      toggleSelectedAppNumber(pba_number);
    });
    dispatch(publisherReportsFiltersTableActions.clearAllFiltersExceptApps());
  };

  useEffect(() => {
    if (!boxRef.current) return;
    const observer = new MutationObserver(() => {
      const children = Array.from(boxRef.current.children);
      childCount$.value = children.length;
      const containerWidth = boxRef.current.clientWidth;
      elemCountInFirstRow$.value = 0;
      const totalChildrenWidth = children.reduce((total, child, index) => {
        const childWidth = child.clientWidth + 3;
        const newTotal = total + childWidth;
        const totalAndBtn = newTotal;
        const childRect = child.getBoundingClientRect();
        const containerRect = boxRef.current.getBoundingClientRect();
        const leftPos = childRect.right;
        if (index + 1 === children.length) {
          showLessBtnLeftPosition$.value = leftPos - containerRect.left;
        }
        if (totalAndBtn <= containerWidth) {
          plusBtnLeftPosition$.value = newTotal;
          elemCountInFirstRow$.value += 1;
        }
        return newTotal;
      }, 0);
      setFitsInOneRow(totalChildrenWidth <= containerWidth);
    });
    observer.observe(boxRef.current, { childList: true });
    return () => observer.disconnect();
  }, []);

  const foundedApps = isItemSelected(applicationsMap$.value, selectedAppsNumbers$.value);
  const foundedAdv = isItemSelected(advertiserList$.value, selectedAdvertisersArr);
  const foundedPlacement = isItemSelected(placementList$.value, selectedPlacementsArr);
  const foundedCountries = isItemSelected(countryList$.value, selectedCountriesArr);

  const allFalse = !foundedApps && !foundedAdv && !foundedPlacement && !foundedCountries;

  return (
    <S.Container isHide={allFalse}>
      <S.WrapperBox ref={wrapperBoxRef}>
        <S.Box ref={boxRef} height={boxHeight$.value}>
          <Each
            of={selectedAppsNumbers$.value}
            render={(pba_number, index) => {
              const selectedApp = getSelectedItem(applicationsMap$.value, pba_number);
              if (!selectedApp) return null;
              return (
                <S.WrapItem>
                  <S.Item>
                    <Label text='Application:' variant='description' style={labelStyle} />
                    <Label
                      variant='label'
                      text={<ParagraphWithTooltip text={selectedApp?.apps ?? ''} />}
                      style={subLabelStyle}
                    />
                    <S.IconWrap
                      onClick={() => handleDelete({ element: 'app', uniqueKey: pba_number })}
                    >
                      <DeleteIcon />
                    </S.IconWrap>
                  </S.Item>
                </S.WrapItem>
              );
            }}
          />
          <Each
            of={selectedPlacementsArr}
            render={(placement_id, index) => {
              const selectedPlacement = getSelectedItem(placementList$.value, placement_id);
              if (!selectedPlacement) return null;
              return (
                <S.WrapItem>
                  <S.Item>
                    <Label text='Placement:' variant='description' style={labelStyle} />
                    <Label
                      variant='label'
                      text={<ParagraphWithTooltip text={selectedPlacement?.placement ?? ''} />}
                      style={subLabelStyle}
                    />
                    <S.IconWrap
                      onClick={() =>
                        handleDelete({ element: 'placement', uniqueKey: placement_id })
                      }
                    >
                      <DeleteIcon />
                    </S.IconWrap>
                  </S.Item>
                </S.WrapItem>
              );
            }}
          />
          <Each
            of={selectedAdvertisersArr}
            render={(adveriser_id, index) => {
              const selectedAdveriser = getSelectedItem(advertiserList$.value, adveriser_id);
              if (!selectedAdveriser) return null;
              return (
                <S.WrapItem>
                  <S.Item>
                    <Label text='Advertiser:' variant='description' style={labelStyle} />
                    <Label
                      variant='label'
                      text={<ParagraphWithTooltip text={selectedAdveriser?.advertiser ?? ''} />}
                      style={subLabelStyle}
                    />
                    <S.IconWrap
                      onClick={() =>
                        handleDelete({ element: 'advertiser', uniqueKey: adveriser_id })
                      }
                    >
                      <DeleteIcon />
                    </S.IconWrap>
                  </S.Item>
                </S.WrapItem>
              );
            }}
          />
          <Each
            of={selectedCountriesArr}
            render={(country_code, index) => {
              const selectedCountry = getSelectedItem(countryList$.value, country_code);
              if (!selectedCountry) return null;
              const my_country_code =
                selectedCountry.country_code === 'UK' ? 'GB' : selectedCountry.country_code;
              return (
                <S.WrapItem>
                  <S.Item>
                    <Label text='Country:' variant='description' style={labelStyle} />
                    <ReactCountryFlag
                      countryCode={my_country_code}
                      svg
                      style={{
                        width: '20px',
                        height: '15px',
                        marginRight: '8px',
                      }}
                    />
                    <Label
                      variant='label'
                      text={
                        <ParagraphWithTooltip
                          text={
                            `${selectedCountry.country} - ${selectedCountry.country_code}` ?? ''
                          }
                        />
                      }
                      style={countrySubLabelStyle}
                    />
                    <S.IconWrap
                      onClick={() => handleDelete({ element: 'country', uniqueKey: country_code })}
                    >
                      <DeleteIcon />
                    </S.IconWrap>
                  </S.Item>
                </S.WrapItem>
              );
            }}
          />
        </S.Box>
        {useComputed(() => {
          if (boxHeight$.value === 'auto') return null;
          if (childCount$.value - elemCountInFirstRow$.value === 0) return null;
          return (
            <S.PlusBtnWrap leftPos={plusBtnLeftPosition$.value}>
              <Buttons.TertiarySmall
                id='showMoreButton'
                onClick={() => {
                  boxHeight$.value = 'auto';
                }}
              >
                {`+ ${childCount$.value - elemCountInFirstRow$.value}`}
              </Buttons.TertiarySmall>
            </S.PlusBtnWrap>
          );
        })}
        {useComputed(() => {
          if (boxHeight$.value === '32px') return null;
          if (childCount$.value === elemCountInFirstRow$.value) return null;
          return (
            <S.ShowLessBtnWrap leftPos={showLessBtnLeftPosition$.value}>
              <Buttons.TertiarySmall
                id='showMoreButton'
                onClick={() => {
                  boxHeight$.value = '32px';
                }}
              >
                Show Less
              </Buttons.TertiarySmall>
            </S.ShowLessBtnWrap>
          );
        })}
      </S.WrapperBox>
      <div>
        <Buttons.TertiarySmall id='showMoreButton' onClick={deleteAll}>
          Clear All Filters
        </Buttons.TertiarySmall>
      </div>
    </S.Container>
  );
};
